/**
 * Crisp chat identification helpers.
 *
 * Crisp is loaded lazily on /app routes only, with CRISP_TOKEN_ID set before
 * l.js injects. That gives session continuity (no duplicate visitors) without
 * calling session:reset on login — which was hiding the chat launcher.
 *
 * @see https://docs.crisp.chat/guides/chatbox-sdks/web-sdk/session-continuity/
 */

import type { User } from '@supabase/supabase-js';
import { reportErrorSilent } from './errorReporter';

const CRISP_WEBSITE_ID = '6ac559dc-dca5-43f0-8fa0-eaeed9751470';

declare global {
  interface Window {
    $crisp: any[] & {
      get?: (method: string) => unknown;
      push: (command: any[]) => void;
    };
    CRISP_TOKEN_ID?: string | null;
    CRISP_WEBSITE_ID?: string;
    CRISP_RUNTIME_CONFIG?: { session_merge?: boolean };
    Crisp?: unknown;
  }
}

type CrispUser = Pick<User, 'id' | 'email' | 'user_metadata'>;

let pendingUser: CrispUser | null = null;
let activeUserId: string | null = null;
let crispScriptInjected = false;
let hooksInstalled = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

function crispAvailable(): boolean {
  return Boolean(window.$crisp && typeof window.$crisp.push === 'function');
}

function resolveUserEmail(user: CrispUser): string | null {
  if (user.email) return user.email;
  const metaEmail = (user.user_metadata as { email?: string } | undefined)
    ?.email;
  return metaEmail || null;
}

function getCrispEmail(): string | null {
  if (!crispAvailable() || typeof window.$crisp.get !== 'function') {
    return null;
  }

  try {
    const email = window.$crisp.get('user:email');
    return typeof email === 'string' && email.length > 0 ? email : null;
  } catch {
    return null;
  }
}

function setCrispEmail(email: string) {
  window.$crisp.push(['set', 'user:email', [email]]);

  const crisp = window.$crisp as any;
  if (typeof crisp?.user?.setEmail === 'function') {
    crisp.user.setEmail(email);
  } else if (typeof crisp?.user?.email?.set === 'function') {
    crisp.user.email.set(email);
  }
}

function applyCrispUserData(user: CrispUser) {
  if (!crispAvailable()) return;

  const email = resolveUserEmail(user);
  if (!email) return;

  const nickname = user.user_metadata?.full_name || email.split('@')[0];
  const avatarUrl = user.user_metadata?.avatar_url;

  try {
    const currentEmail = getCrispEmail();
    if (!currentEmail || currentEmail.toLowerCase() !== email.toLowerCase()) {
      setCrispEmail(email);
    }

    if (nickname) {
      window.$crisp.push(['set', 'user:nickname', [nickname]]);
    }

    if (avatarUrl) {
      window.$crisp.push(['set', 'user:avatar', [avatarUrl]]);
    }

    window.$crisp.push([
      'set',
      'session:data',
      [
        [
          ['user_id', user.id],
          ['email', email],
          ['nickname', nickname],
        ],
      ],
    ]);
  } catch (error) {
    reportErrorSilent({
      context: 'Crisp: apply user data',
      error: error as Error,
    });
  }
}

function clearRetryTimer() {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

function scheduleEmailRetries(user: CrispUser) {
  clearRetryTimer();

  const email = resolveUserEmail(user);
  if (!email) return;

  const delays = [400, 1500];
  let attempt = 0;

  const retry = () => {
    if (pendingUser?.id !== user.id) return;

    const currentEmail = getCrispEmail();
    if (!currentEmail || currentEmail.toLowerCase() !== email.toLowerCase()) {
      setCrispEmail(email);
    }

    attempt += 1;
    if (attempt < delays.length) {
      retryTimer = setTimeout(retry, delays[attempt]);
    }
  };

  retryTimer = setTimeout(retry, delays[0]);
}

function onCrispSessionReady() {
  if (!pendingUser) return;
  applyCrispUserData(pendingUser);
  scheduleEmailRetries(pendingUser);
}

function installCrispHooks() {
  if (hooksInstalled || !crispAvailable()) return;

  hooksInstalled = true;
  window.$crisp.push(['on', 'session:loaded', onCrispSessionReady]);
  window.$crisp.push(['on', 'chat:opened', onCrispSessionReady]);
}

function injectCrispScript() {
  if (crispScriptInjected) return;
  crispScriptInjected = true;

  window.$crisp = window.$crisp || [];
  window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
  window.CRISP_RUNTIME_CONFIG = { session_merge: true };

  const script = document.createElement('script');
  script.src = 'https://client.crisp.chat/l.js';
  script.async = true;
  document.head.appendChild(script);
}

/**
 * Load Crisp for a logged-in user and set their email once the session is ready.
 * Safe to call on every render — only injects the script once per page load.
 */
export function initCrispForUser(user: CrispUser) {
  if (!resolveUserEmail(user)) return;

  pendingUser = user;

  // Same user, script already injected — nothing to do.
  if (activeUserId === user.id && crispScriptInjected) return;

  // Different user signed in on the same tab (rare) — reset to switch sessions.
  if (
    activeUserId &&
    activeUserId !== user.id &&
    crispScriptInjected &&
    crispAvailable()
  ) {
    activeUserId = user.id;
    window.CRISP_TOKEN_ID = user.id;
    installCrispHooks();
    window.$crisp.push(['do', 'session:reset']);
    return;
  }

  activeUserId = user.id;

  // Token must be set before l.js loads (no session:reset needed on first load).
  window.CRISP_TOKEN_ID = user.id;
  window.$crisp = window.$crisp || [];
  installCrispHooks();
  injectCrispScript();
}

/** @deprecated Use initCrispForUser */
export const identifyCrispUser = initCrispForUser;

/** Hide or show the Crisp launcher so it does not cover the product tour. */
export function setCrispChatVisible(visible: boolean) {
  if (!crispAvailable()) return;
  try {
    window.$crisp.push(['do', visible ? 'chat:show' : 'chat:hide']);
  } catch (error) {
    reportErrorSilent({
      context: 'Crisp: toggle chat visibility',
      error: error as Error,
    });
  }
}

/**
 * Clear the user's Crisp session on logout.
 */
export function resetCrispSession() {
  pendingUser = null;
  activeUserId = null;
  clearRetryTimer();

  if (!crispAvailable()) return;

  try {
    window.CRISP_TOKEN_ID = null;
    window.$crisp.push(['do', 'session:reset']);
    window.$crisp.push(['do', 'chat:hide']);
  } catch (error) {
    reportErrorSilent({
      context: 'Crisp: session reset',
      error: error as Error,
    });
  }
}
